// Story 013 — single BBox-cropped tile rendered as a `role="option"`
// button. The crop draws to a `<canvas>` from urls.full client-side
// (Christian's note 2026-05-10: thumb_900 was making tiles blurry;
// using urls.full at native ~6000 px and downscaling gives crisp crops
// that read cleanly). On `<img onerror>` (e.g. local-dev placeholder,
// network blip) we step down to urls.thumb900 — never blank.
//
// Crop algorithm (ANA-R3-TILE-03 — zoom-to-fit-then-cap, replaces the old
// buffer-ratio + MIN_SOURCE_CROP_PX=300 floor). The pure math lives in
// `tileCrop.ts::computeTileCrop`; per tile we:
//   * Pad the bbox by ~20 source px, square it → the ideal region.
//   * Fit-zoom the region to the tile, then CLAMP the zoom to the user's
//     `tiles.maxZoomPercent` cap (default 1000 %, Settings 100–2000 %). So a
//     tiny defect magnifies up to the cap (no hyper-zoom); a large defect
//     fits with ~20 px context; a defect bigger than the tile zooms OUT.
//   * Centre on the shape centroid; clamp the crop inside the image bounds.
//   * BBox rectangle drawn ON the tile so the user sees where the defect is.
//   * Tile rendered square at the chosen size preset (small/medium/large),
//     with the canvas backing store sized to the device-pixel cell so crops
//     stay crisp on HiDPI.
import { useEffect, useRef, useState } from "react";
import { Box, Chip } from "@mui/material";
import type { AiFinding, ImageRef } from "@aeriosiq/types";
import { useTileSettings } from "../viewer/useTileSettings.js";
import { TILE_SIZE_PX, computeTileCrop } from "./tileCrop.js";
// ANA-R3-GEOM-05 — on-tile zoomable polygon editor + the session-only override
// store. When a finding carries a session override the tile renders the
// MODIFIED outline (and reframes the crop around it), hides the original, and
// shows an "edited" badge; hover/focus reveals the editor (OQ2 — all sizes).
import { useTileGeometryOverrideStore } from "../tiles/tileGeometryOverrideStore.js";
import { TileGeometryEditor } from "../tiles/TileGeometryEditor.js";
import { zoomedTileCrop, type TileLocalView } from "../tiles/tileTransform.js";

// ANA-R4-TILE-02 — the identity mini-viewport (no zoom/pan).
const TILE_VIEW_IDENTITY: TileLocalView = {
  editZoom: 1,
  panX: 0,
  panY: 0,
  version: 0,
};

// ANA-R4-GEOM-02 — AI-finding geometry is BLUE (defects are orange). This is an
// AI-finding tile, so the unedited outline uses the AI signal-blue
// (AI_BBOX_DEFAULT_COLOR #0071E3), matching the Viewer's AI overlay. Was #FF8800.
const BBOX_STROKE = "#0071E3";
const BBOX_STROKE_WIDTH = 2;
// ANA-R3-GEOM-05 — a session-edited (override) outline draws in the accent
// blue so an edited tile reads as distinct from an unedited (orange) one even
// before the "edited" badge is noticed.
const OVERRIDE_STROKE = "#2F6FED";
// ANA-R3-TILE-03 — backing-store device-pixel-ratio clamp. The canvas is
// sized to the device-pixel cell so crops stay crisp on HiDPI displays
// without exploding the backing store on extreme DPRs.
const MAX_DPR = 3;

export interface TileProps {
  finding: AiFinding;
  image: ImageRef;
  selected: boolean;
  active: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * ANA-R1-04 — the user-facing class label for the tile's accessible name.
   * The model display name everywhere user-facing; raw `class_name` never is.
   * Defaults to `finding.aiClassName` for callers that pass an already-resolved
   * label via the finding (e.g. the defect pseudo-finding's catalogue label).
   */
  displayLabel?: string;
  /** Optional overlay rendered absolutely on top of the canvas (e.g. the
   *  defect severity/label badge). Lives inside the button so axe's
   *  `aria-allowed-children` rule on `role="listbox"` parents doesn't
   *  flag a sibling wrapper div. */
  overlay?: React.ReactNode;
  /**
   * ANA-R3-GEOM-05 — when true, hover/focus reveals the on-tile polygon editor
   * and a session override (if present) is honoured. AI-finding tiles set this;
   * defect pseudo-finding tiles (DefectTile) leave it false — defect geometry
   * is edited in the Viewer (GEOM-04), not promoted from a tile.
   */
  editable?: boolean;
  /**
   * ANA-R4-TILE-DEFECT-COLOR — the unedited outline stroke colour. AI-finding
   * tiles pass the AI bbox colour (blue); DEFECT tiles pass the defect bbox
   * colour (orange) — both from the user's settings. Defaults to the AI blue.
   */
  outlineColor?: string;
  /**
   * ANA-R3-TILE-02 — render-progress signal. Fired exactly ONCE per tile when
   * the crop has settled: in the `draw()` success path (image loaded + canvas
   * painted, incl. the thumb900 fallback) AND in the terminal `onerror` path
   * (both `full` and `thumb900` failed — a tile that can't paint still settles
   * so the widget's progress bar can reach 100% and never hangs one short).
   * The TilesWidget aggregates these into a `Set<settledId>`; `id` is the
   * finding/defect id. Guarded so a late fire after unmount/re-render is
   * suppressed (reuses the draw effect's `cancelled` flag).
   */
  onSettled?: ((id: string) => void) | undefined;
}

export function Tile({
  finding,
  image,
  selected,
  active,
  onClick,
  overlay,
  displayLabel,
  editable = false,
  outlineColor,
  onSettled,
}: TileProps): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // ANA-R3-TILE-02 — keep the latest `onSettled` in a ref so the draw effect
  // doesn't re-run (and re-download the image) just because the parent passed a
  // fresh callback identity each render. The effect reads `onSettledRef.current`
  // at fire time; it is NOT in the effect's dependency array.
  const onSettledRef = useRef<TileProps["onSettled"]>(onSettled);
  onSettledRef.current = onSettled;
  // ANA-R3-TILE-03 — tile size preset + max-zoom cap (per-user prefs). The
  // crop re-runs whenever either changes, so dragging the slider / switching
  // size re-renders all visible tiles live (AC3).
  const { tiles } = useTileSettings();
  const tileSidePx = TILE_SIZE_PX[tiles.tileSize];
  const maxZoomPercent = tiles.maxZoomPercent;

  // ANA-R3-GEOM-05 — the finding's session override (normalised serialised
  // path), if any. Only consulted for editable (AI-finding) tiles.
  const override = useTileGeometryOverrideStore((s) =>
    editable ? (s.overrides[finding.id] ?? null) : null,
  );
  const [editorOpen, setEditorOpen] = useState(false);
  // ANA-R4-TILE-02 — the shared local zoom/pan. Owned here so the IMAGE canvas
  // and the editor geometry zoom together; reset to identity when editing ends.
  const [tileView, setTileView] = useState<TileLocalView>(TILE_VIEW_IDENTITY);
  // Cache the decoded crop image so a zoom/pan re-draw doesn't re-fetch it.
  const loadedImgRef = useRef<{ url: string; img: HTMLImageElement } | null>(
    null,
  );
  useEffect(() => {
    if (!editorOpen) setTileView(TILE_VIEW_IDENTITY);
  }, [editorOpen]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const bb = finding.bbox;
    // ANA-R3-TILE-02 — a tile with no bbox has nothing to draw; settle it
    // immediately so it still counts toward the progress total (defensive — the
    // widget only mounts `Tile` with a bbox-bearing finding, but never hang).
    if (!bb) {
      onSettledRef.current?.(finding.id);
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // ANA-R3-TILE-03 — size the backing store to the device-pixel cell so
    // crops stay crisp on HiDPI; the CSS box stays at the preset side.
    const dpr = Math.min(
      MAX_DPR,
      typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
    );
    const backing = Math.round(tileSidePx * dpr);
    if (canvas.width !== backing) canvas.width = backing;
    if (canvas.height !== backing) canvas.height = backing;

    let cancelled = false;
    // ANA-R3-TILE-02 — fire `onSettled` at most once per effect run. `settled`
    // is local to this run (a re-render that re-keys the effect — image/bbox/
    // override/size change — starts a fresh settle for the fresh crop, which is
    // what the progress reset wants). `cancelled` suppresses a fire after
    // unmount or after the deps changed mid-load.
    let settled = false;
    const settle = (): void => {
      if (cancelled || settled) return;
      settled = true;
      onSettledRef.current?.(finding.id);
    };

    const draw = (img: HTMLImageElement): void => {
      if (cancelled) return;
      const imgW = img.width;
      const imgH = img.height;

      // ANA-R3-GEOM-05 — the EFFECTIVE outline drawn on the tile is the session
      // override when present, else the finding's true `path`, else its bbox
      // rectangle (back-compat). The crop reframes around the OVERRIDE's bbox
      // when present (crop framing follows the override — story §2), so the
      // edited shape stays centred + the original is never shown.
      const overridePath = override?.path ?? null;
      const framingBbox = overridePath
        ? (normPathAabb(overridePath) ?? { x: bb.x, y: bb.y, w: bb.w, h: bb.h })
        : { x: bb.x, y: bb.y, w: bb.w, h: bb.h };

      // Zoom-to-fit-then-cap (tileCrop.ts). `tileDisplayPx` is the LOGICAL
      // tile side — the cap is a percentage of native resolution, so it is
      // independent of the device-pixel backing store.
      const base = computeTileCrop({
        bbox: framingBbox,
        imgW,
        imgH,
        tileDisplayPx: tileSidePx,
        maxZoomPercent,
      });

      // ANA-R4-TILE-02 — while editing, compose the base crop with the shared
      // local zoom/pan and RE-DRAW the canvas from the zoomed SOURCE region (so
      // the image stays crisp, not a blurry CSS scale) — the geometry overlay
      // below uses the SAME crop, so image + polygon zoom together. The editor's
      // `createTileTransform(frame, view)` is the exact inverse of this crop, so
      // handles line up with the re-drawn image.
      const zoomed =
        editorOpen &&
        (tileView.editZoom !== 1 || tileView.panX !== 0 || tileView.panY !== 0);
      const { cropSide, sx, sy } = zoomed
        ? zoomedTileCrop(
            { ...base, imgW, imgH, tileDisplayPx: tileSidePx },
            tileView,
          )
        : base;

      ctx.imageSmoothingQuality = "high";
      ctx.clearRect(0, 0, backing, backing);
      ctx.drawImage(img, sx, sy, cropSide, cropSide, 0, 0, backing, backing);

      // The crop is square + same scale on both axes, so projecting a
      // normalised image point into backing-store px is one ratio.
      const scale = backing / cropSide;
      const projX = (nx: number): number => (nx * imgW - sx) * scale;
      const projY = (ny: number): number => (ny * imgH - sy) * scale;

      ctx.lineWidth = BBOX_STROKE_WIDTH * dpr;
      // ANA-R4-TILE-DEFECT-COLOR — unedited outline uses the per-kind settings
      // colour (AI blue / defect orange) passed by the host; an edited override
      // keeps the accent blue. Falls back to the AI blue default.
      ctx.strokeStyle = override
        ? OVERRIDE_STROKE
        : (outlineColor ?? BBOX_STROKE);

      // Prefer the actual outline (override or stored finding path) — a flat
      // `[x0,y0,…]` NORMALISED array; closed iff the last pair == the first.
      const outline = overridePath ?? finding.path ?? null;
      if (outline && outline.length >= 4) {
        ctx.beginPath();
        let started = false;
        for (let i = 0; i + 1 < outline.length; i += 2) {
          const nx = outline[i];
          const ny = outline[i + 1];
          if (typeof nx !== "number" || typeof ny !== "number") continue;
          const px = projX(nx);
          const py = projY(ny);
          if (!started) {
            ctx.moveTo(px, py);
            started = true;
          } else {
            ctx.lineTo(px, py);
          }
        }
        // The serialised closed ring already repeats the first vertex; for a
        // raw stored path that doesn't, close the path visually.
        ctx.stroke();
      } else {
        // Back-compat: no path on the wire → draw the derived bbox rect.
        const tileBoxX = projX(bb.x);
        const tileBoxY = projY(bb.y);
        const tileBoxW = bb.w * imgW * scale;
        const tileBoxH = bb.h * imgH * scale;
        ctx.strokeRect(tileBoxX, tileBoxY, tileBoxW, tileBoxH);
      }

      // ANA-R3-TILE-02 — the crop has finished painting: this tile is settled.
      // (The thumb900 fallback also reaches here once it paints.)
      settle();
    };

    const loadFromUrl = (url: string, onFail?: () => void): void => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        // ANA-R4-TILE-02 — cache the decoded image so a zoom/pan re-draw (the
        // effect re-runs on `tileView`) paints from cache without re-fetching.
        loadedImgRef.current = { url, img };
        draw(img);
      };
      img.onerror = () => {
        if (onFail) {
          onFail();
        } else {
          // ANA-R3-TILE-02 — terminal failure: both `full` and `thumb900` have
          // errored, so nothing will ever paint. Settle anyway so the progress
          // bar reaches 100% and never hangs one short (AC3).
          settle();
        }
      };
      img.src = url;
    };

    // ANA-R4-TILE-02 — a zoom/pan re-draw re-runs this effect; if the image is
    // already decoded (same url), paint straight from cache (no network, no
    // flicker). The crop numbers above pick up the new `tileView`.
    const cached = loadedImgRef.current;
    if (
      cached &&
      (cached.url === image.urls.full || cached.url === image.urls.thumb900)
    ) {
      draw(cached.img);
      return () => {
        cancelled = true;
      };
    }

    // Try full-res first for crisp crops; fall back to thumb_900 on
    // any load failure (placeholder PNG, CORS, network blip).
    loadFromUrl(image.urls.full, () => {
      loadFromUrl(image.urls.thumb900);
    });

    return () => {
      cancelled = true;
    };
  }, [
    finding.id,
    finding.bbox,
    finding.path,
    override,
    image.urls.full,
    image.urls.thumb900,
    tileSidePx,
    maxZoomPercent,
    outlineColor,
    // ANA-R4-TILE-02 — re-draw (from cache) on zoom/pan + when editing toggles.
    editorOpen,
    tileView,
  ]);

  return (
    <Box
      component="button"
      role="option"
      aria-selected={selected ? "true" : "false"}
      aria-current={active ? "true" : "false"}
      aria-label={`Select ${displayLabel ?? finding.aiClassName} finding`}
      onClick={onClick}
      {...(editable
        ? {
            onMouseEnter: () => {
              setEditorOpen(true);
            },
            onMouseLeave: () => {
              setEditorOpen(false);
            },
            onFocus: () => {
              setEditorOpen(true);
            },
            onBlur: (e: React.FocusEvent<HTMLButtonElement>) => {
              // Keep the editor open while focus stays inside the tile (e.g. on
              // the editor surface / its toolbar buttons).
              if (!e.currentTarget.contains(e.relatedTarget)) {
                setEditorOpen(false);
              }
            },
          }
        : {})}
      sx={{
        position: "relative",
        width: tileSidePx,
        height: tileSidePx,
        p: 0,
        border: "none",
        borderRadius: 1,
        cursor: "pointer",
        backgroundColor: "background.paper",
        // R58-L-10 — selected state is a **white frame** per R58-36 +
        // `00-epic.md` §5 (12 Jan epic). The active state (the single
        // focused tile that drives the Viewer overlay) keeps its primary
        // ring so multi-selection vs activation stay visually distinct.
        //
        // ANA-R2-09 — the selection ring is an INSET box-shadow, not a CSS
        // `outline`. An outline ignores `border-radius` and draws square
        // corners, so the ring did not match the tile's rounded corners.
        // An inset box-shadow follows the element's `border-radius`, so the
        // ring now shares the tile radius automatically (no radius literal).
        // ANA-R4-TILE-04 — a selected tile gets a BLUE ring matching the
        // film-strip selected-image style (was white #FFFFFF). `active` stays a
        // thicker primary.main ring so active vs selected remain distinguishable.
        // ANA-R4-TILE-04-FIX — the selection/active ring is now an OVERLAY
        // element (below), painted ON TOP of the canvas. An inset box-shadow
        // here was hidden behind the opaque <canvas> child, so selecting a tile
        // showed no border at all.
        "&:focus-visible": {
          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "-2px",
        },
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          borderRadius: 4,
        }}
        aria-hidden="true"
      />
      {/* ANA-R4-TILE-04-FIX — selection / active ring drawn ABOVE the canvas so
          it is actually visible (the inset box-shadow on the button was covered
          by the opaque canvas). Follows the tile's border-radius; pointer-events
          none so it never blocks selection clicks. Selected = film-strip blue
          (primary.light); active = a thicker primary.main ring (distinct). */}
      {selected || active ? (
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "4px",
            pointerEvents: "none",
            boxSizing: "border-box",
            border: (theme) =>
              active
                ? `3px solid ${theme.palette.primary.main}`
                : `2px solid ${theme.palette.primary.light}`,
          }}
        />
      ) : null}
      {overlay}
      {/* ANA-R3-GEOM-05 — "edited" badge marks a tile with a session override
          (AC2). It sits top-right so it never collides with the editor toolbar
          (top-left) or the defect label (bottom). */}
      {editable && override ? (
        <Chip
          label="Edited"
          size="small"
          color="primary"
          data-testid="tile-edited-badge"
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            height: 18,
            fontSize: 10,
            fontWeight: 600,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      ) : null}
      {/* On-tile zoomable polygon editor — revealed on hover/focus (AC1).
          Mounted inside the button (its root is role="application", allowed
          under role="option"). A click on EMPTY editor space bubbles up to the
          tile's select handler (so the tile stays selectable while the editor
          is shown); only vertex drags + the editor toolbar buttons consume the
          interaction. The toolbar buttons stop propagation themselves so a
          Save/Reset/convert never also selects the tile. */}
      {editable && editorOpen && finding.bbox ? (
        <Box component="span" sx={{ position: "absolute", inset: 0 }}>
          <TileGeometryEditor
            finding={finding}
            imgW={image.width}
            imgH={image.height}
            tileDisplayPx={tileSidePx}
            maxZoomPercent={maxZoomPercent}
            view={tileView}
            onViewChange={setTileView}
            onClose={() => {
              setEditorOpen(false);
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}

/** AABB (normalised) of a flat `[x0,y0,…]` path, or null when degenerate. */
function normPathAabb(
  flat: number[],
): { x: number; y: number; w: number; h: number } | null {
  if (flat.length < 4) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < flat.length; i += 2) {
    const x = flat[i];
    const y = flat[i + 1];
    if (typeof x !== "number" || typeof y !== "number") continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (!Number.isFinite(minX)) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
